import { Link, useForm, usePage } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Button, { ButtonLink } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";
import formatTime from "@/utils/formatTime";
import type { SharedProps } from "@/types";

export type EventBudgetSummary = {
  venue_confirmed: boolean;
  hcb_ready: boolean;
  missing_hcb_address: string[];
  hcb_stage: "blocked" | "ready_to_request" | "awaiting_url" | "ready_to_fund";
  hcb_stage_label: string;
  hcb_url: string | null;
  hcb_requested: boolean;
  verified_rsvps: number;
  pending_rsvps: number;
  rate_cents: number;
  earned_cents: number;
  rsvp_cents: number;
  // The RSVP half of the grant is frozen pending a look from HQ. `rsvp_cents`
  // is zero while this is true; `held_rsvp_cents` is what it would be. The
  // reason is deliberately not sent - it names what the review keys on, and
  // the organizer is the party the grant pays.
  funding_held: boolean;
  held_rsvp_cents: number;
  // Which of the two grants this event is on. "rsvps" is paid after the day
  // per verified signup; "sites" is paid before it, per approved website
  // handed in ahead of the start. An event is on one or the other, never both,
  // so exactly one of rsvp_cents and prefunding_cents is ever non-zero.
  funding_model: "rsvps" | "sites";
  prefunding_cents: number;
  funded_sites: number;
  // Handed in ahead of the day and still waiting on a reviewer. This is what
  // says whether the figure above is final.
  pending_sites: number;
  pending_site_cents: number;
  rate_per_site_cents: number;
  coding_cents: number;
  coding_seconds: number;
  in_review_coding_seconds: number;
  rate_per_hour_cents: number;
  projects: {
    id: number;
    title: string | null;
    state: string;
    username: string | null;
    seconds: number;
    cents: number;
  }[];
  rate_country: string | null;
  half_show_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  payouts: {
    id: number;
    amount_cents: number;
    sent_on: string;
    method_label: string;
    reference: string | null;
    note: string | null;
  }[];
};

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

// What the event has earned, and why it hasn't earned more. The two reasons a
// number is lower than an organizer expects are always the same - the venue
// isn't confirmed, or people haven't clicked the link in their email - so both
// are stated rather than left to be worked out.
export default function EventBudget({
  budget,
  eventId,
}: {
  budget: EventBudgetSummary;
  eventId: string | number;
}) {
  const { props } = usePage<SharedProps>();
  const shippingOn = props.program.projects_enabled;
  const bySites = budget.funding_model === "sites";
  const { data, setData, patch, processing, errors } = useForm({
    hcb_url: budget.hcb_url ?? "",
  });

  return (
    <Panel title="budget.txt">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline gap-3">
          <p className="font-display text-display text-leaf font-bold">
            {money(budget.earned_cents)}
          </p>
          <p className="text-caption text-ink-muted">
            {bySites ? "your budget" : "earned so far"}
          </p>
        </div>

        {/* Two sources, so the headline gets broken down rather than left as a
            number nobody can reconcile against their own RSVP count. */}
        <dl className="border-line/20 grid grid-cols-1 gap-3 border-y py-3 sm:grid-cols-2">
          <div>
            <dt className="font-display text-subheading text-ink font-bold">
              {money(bySites ? budget.prefunding_cents : budget.rsvp_cents)}
            </dt>
            <dd className="text-caption text-ink-muted">
              {bySites ? (
                <>
                  {budget.funded_sites} approved{" "}
                  {budget.funded_sites === 1 ? "site" : "sites"} ×{" "}
                  {money(budget.rate_per_site_cents)}
                </>
              ) : (
                <>
                  {budget.verified_rsvps} verified{" "}
                  {budget.verified_rsvps === 1 ? "RSVP" : "RSVPs"} ×{" "}
                  {money(budget.rate_cents)}
                  {budget.rate_country && ` (the ${budget.rate_country} rate)`}
                </>
              )}
            </dd>
          </div>

          {shippingOn && (
            <div>
              <dt className="font-display text-subheading text-ink font-bold">
                {money(budget.coding_cents)}
              </dt>
              <dd className="text-caption text-ink-muted">
                {formatTime(budget.coding_seconds)} approved ×{" "}
                {money(budget.rate_per_hour_cents)} an hour
              </dd>
            </div>
          )}
        </dl>

        {/* How this event gets paid, said plainly. An organizer who has been
            reading about a per-signup grant needs to be told their city works
            differently, and told it where the number is - not in a guide. */}
        {bySites && (
          <div className="border-line bg-surface-plain border-2 p-3">
            <p className="text-body text-ink">
              Your city is funded before the day, not after it. Every site
              someone hands in ahead of your start time is worth{" "}
              <strong>{money(budget.rate_per_site_cents)}</strong> once a
              reviewer approves it.
            </p>
            {/* Said explicitly because the obvious reading of "you aren't paid
                per signup" is "signups don't matter", and that is wrong in the
                way that costs the most: handing in needs a code emailed to an
                address on the roster, so an attendee who never signed up
                cannot hand anything in. */}
            <p className="text-body text-ink-muted mt-2">
              You still need RSVPs to get there. Handing in takes a code we
              email to an address on your roster, so somebody who never signed
              up can't hand a site in at all. Get signups, then get them
              building.
            </p>
            {budget.pending_sites > 0 && (
              <p className="text-caption text-ink-muted mt-2">
                {budget.pending_sites}{" "}
                {budget.pending_sites === 1 ? "site is" : "sites are"} still
                waiting on a reviewer, worth up to another{" "}
                <strong className="text-ink">
                  {money(budget.pending_site_cents)}
                </strong>
                .
              </p>
            )}
          </div>
        )}

        {/* Half the people who RSVP turn up, near enough, so this is the
            number to size a boba order against rather than the headline. Only
            worth saying once there are RSVPs it could apply to, and never on a
            site-funded event, where RSVPs are not what pays. */}
        {!bySites && budget.verified_rsvps > 0 && (
          <p className="text-caption text-ink-muted">
            If half your RSVPs show up, that's about{" "}
            <strong className="text-ink">
              {money(budget.half_show_cents)}
            </strong>{" "}
            per person in the room.
          </p>
        )}

        {/* Said out loud rather than left as a silent zero. An organizer whose
            signup total vanished overnight will otherwise read it as a bug and
            open a thread about it. */}
        {budget.funding_held && !bySites && (
          <div className="border-line bg-surface-plain border-2 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="warning">Signups on hold</Badge>
              <span className="text-body text-ink font-bold">
                We're checking your RSVPs before they count.
              </span>
            </div>
            <p className="text-caption text-ink-muted mt-1">
              {budget.held_rsvp_cents > 0 && (
                <>{money(budget.held_rsvp_cents)} is waiting on this. </>
              )}
              Nobody comes off your roster and your event runs as normal. Your
              coding hours are unaffected. Message HQ in the thread below if you
              think this is wrong.
            </p>
          </div>
        )}

        {/* Scoped to the RSVP half on purpose: coding hours pay out without a
            venue, so "your budget starts once..." would contradict a total
            that's already above zero. */}
        {!budget.venue_confirmed && (
          <div className="border-line bg-surface-plain border-2 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="warning">Venue needed</Badge>
              <span className="text-body text-ink font-bold">
                {bySites
                  ? "We still need your venue before we can send this."
                  : "Your RSVPs start earning once your venue is confirmed."}
              </span>
            </div>
            <p className="text-caption text-ink-muted mt-1">
              {/* A site-funded budget isn't venue-gated the way a per-signup
                  one is - that money is for booking the room, so withholding
                  it until the room is booked is backwards. The venue is still
                  needed, but for the bank account, not to start the counter. */}
              {bySites
                ? "Your budget above isn't waiting on this - the money is what you book the room with. But a bank account needs a real address, so fill in the venue type, name, address and date below."
                : "Fill in the venue type, name, address and date below. Your HCB account gets set up at the same time."}
            </p>
          </div>
        )}

        {/* A confirmed venue with half an address stalls silently on HCB's
            side, so the gap is named here rather than left to be discovered. */}
        {budget.venue_confirmed && budget.missing_hcb_address.length > 0 && (
          <div className="border-line bg-surface-plain border-2 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="warning">Address incomplete</Badge>
              <span className="text-body text-ink font-bold">
                Your HCB account is waiting on{" "}
                {budget.missing_hcb_address.join(", ")}.
              </span>
            </div>
            <p className="text-caption text-ink-muted mt-1">
              A bank account needs a full postal address. Add it below and it
              goes through automatically.
            </p>
          </div>
        )}

        {/* The handover. Once the base has asked HCB to open an account, the
            organizer finishes a form off-platform and ends up with a URL that
            only they can see - so nothing can move until they paste it back. */}
        {budget.hcb_stage === "awaiting_url" && (
          <form
            className="border-line bg-surface-plain flex flex-col gap-2 border-2 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              patch(`/organize/${eventId}`, { preserveScroll: true });
            }}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="info">Account on its way</Badge>
              <span className="text-body text-ink font-bold">
                Paste your HCB link here once it's open.
              </span>
            </div>
            <p className="text-caption text-ink-muted">
              Check your email for the HCB form. Finish it, then copy the
              address of your account. We can't send you anything until we know
              where it goes.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Your HCB link" error={errors.hcb_url}>
                <Input
                  value={data.hcb_url}
                  onChange={(e) => setData("hcb_url", e.target.value)}
                  placeholder="https://hcb.hackclub.com/your-org"
                  className="w-72"
                />
              </Field>
              <Button type="submit" disabled={processing}>
                {processing ? "Saving…" : "Save it"}
              </Button>
            </div>
          </form>
        )}

        {budget.hcb_stage === "ready_to_fund" && budget.hcb_url && (
          <p className="text-caption text-ink-muted">
            Your money goes to{" "}
            <a
              className="text-link underline"
              href={budget.hcb_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {budget.hcb_url.replace("https://", "")}
            </a>
            . Tell HQ in your thread if that's wrong.
          </p>
        )}

        {/* Earned is not the same as arrived. HQ sends the money from HCB by
            hand, so the two numbers drift apart for a few days and organizers
            ask where theirs is -- this is the answer. */}
        {budget.earned_cents > 0 && (
          <div className="border-line/20 flex flex-col gap-2 border-t pt-3">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-caption text-ink-muted">
                Sent to your HCB account so far:{" "}
                <strong className="text-ink">{money(budget.paid_cents)}</strong>
              </span>
              {budget.outstanding_cents > 0 && (
                <span className="text-caption text-ink-muted">
                  On its way:{" "}
                  <strong className="text-ink">
                    {money(budget.outstanding_cents)}
                  </strong>
                </span>
              )}
            </div>

            {budget.payouts.length > 0 ? (
              <div className="flex flex-col gap-1">
                {budget.payouts.map((payout) => (
                  <p key={payout.id} className="text-caption text-ink-soft">
                    {money(payout.amount_cents)} on {payout.sent_on} ·{" "}
                    {payout.method_label}
                    {payout.reference && ` · ${payout.reference}`}
                    {payout.note && ` · ${payout.note}`}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-caption text-ink-soft">
                Nothing sent yet. This is what your event has earned so far; it
                moves once your HCB account is open. Ask in your thread if
                you're not sure where yours is.
              </p>
            )}
          </div>
        )}

        {/* The other lever an organizer has. RSVPs depend on other people
            turning up; hours are entirely in their own hands, so the ask is
            stated rather than left in a handbook. */}
        {shippingOn && (
          <div className="border-line/20 flex flex-col gap-3 border-t pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-display text-subheading text-ink font-bold">
                Ships
              </h3>
              <ButtonLink href="/projects" variant="secondary" size="sm">
                {budget.projects.length > 0
                  ? "Your projects"
                  : "Ship something"}
              </ButtonLink>
            </div>

            {budget.projects.length === 0 ? (
              <p className="text-caption text-ink-muted">
                Connect Hackatime, build something, and ship it for this event.
                Every hour a reviewer approves adds{" "}
                <strong className="text-ink">
                  {money(budget.rate_per_hour_cents)}
                </strong>{" "}
                to the budget above.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {budget.projects.map((project) => (
                  <li
                    key={project.id}
                    className="text-caption text-ink-muted flex flex-wrap items-baseline justify-between gap-2"
                  >
                    <Link
                      href={`/projects/${project.id}/manage`}
                      className="text-link min-w-0 truncate underline"
                    >
                      {project.title ?? "Untitled project"}
                    </Link>
                    <span className="shrink-0">
                      {project.state === "approved" ? (
                        <>
                          {formatTime(project.seconds)} ·{" "}
                          <strong className="text-ink">
                            {money(project.cents)}
                          </strong>
                        </>
                      ) : project.state === "submitted" ? (
                        <>{formatTime(project.seconds)} under review</>
                      ) : project.state === "rejected" ? (
                        "sent back"
                      ) : (
                        "not shipped yet"
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {budget.in_review_coding_seconds > 0 && (
              <p className="text-caption text-ink-soft">
                {formatTime(budget.in_review_coding_seconds)} is with a reviewer
                - worth about{" "}
                {money(
                  Math.round(
                    (budget.in_review_coding_seconds / 3600) *
                      budget.rate_per_hour_cents,
                  ),
                )}{" "}
                once it's approved.
              </p>
            )}
          </div>
        )}

        {budget.pending_rsvps > 0 && (
          <p className="text-caption text-ink-muted">
            {budget.pending_rsvps} more{" "}
            {budget.pending_rsvps === 1 ? "person hasn't" : "people haven't"}{" "}
            confirmed their email yet, so they don't count yet. They also count
            once you check them in on the day.
          </p>
        )}
      </div>
    </Panel>
  );
}
